/**
 * components/instructor/SidePanel.jsx
 * Session is instructor-scoped — no course selector needed to open.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './SidePanel.module.css';

/**
 * Live countdown to the pooled-shuffle deadline. Shows mm:ss ticking down;
 * flips to a "shuffling now" note at zero (the actual shuffle is server-side,
 * fired by the scheduler or the next status read).
 */
function PoolCountdown({ deadline }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!deadline) return null;
  const msLeft = new Date(deadline).getTime() - now;

  if (msLeft <= 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600 }}>
        Shuffling now…
      </div>
    );
  }
  const total = Math.floor(msLeft / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return (
    <div style={{ fontSize: 13, color: 'var(--txt)' }}>
      Shuffle in{' '}
      <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, color: 'var(--amber)' }}>
        {mm}:{ss}
      </span>
    </div>
  );
}

const POLICIES = [
  { value: 'FCFS',        label: 'First-Come, First-Served' },
  { value: 'POINTS',      label: 'Points-Based', disabled: true },
  { value: 'RANDOMIZED',  label: 'Randomized' },
  { value: 'POOLED_FCFS', label: 'Pooled FCFS' },
];

export default function SidePanel({
  open,
  session,
  policy,
  isPaused,
  isLocked,
  courses,
  classPriorityCourseIds,
  onOpenSession,
  onCloseSession,
  onTogglePause,
  onToggleLock,
  onChangePolicy,
  onAnnounce,
  onSetClassPriority,
  onForceShuffle,
  blockNonPriority,
  onToggleBlockNonPriority,
  onRemoveNonPriority,
  testStudents,
  onAddTestStudent,
  onViewAsTestStudent,
  entries,
  onCallNext,
  pooling,
  loading,
}) {
  // Default to the first policy that isn't disabled, so a disabled option can
  // never be the pre-selected value when opening a session.
  const firstEnabledPolicy = (POLICIES.find(p => !p.disabled) || POLICIES[0]).value;
  const [selectedPolicy,   setSelectedPolicy]   = useState(firstEnabledPolicy);
  const [announcementText, setAnnouncementText] = useState('');
  const [confirmClose,     setConfirmClose]     = useState(false);

  // Per-session message overrides (blank = fall back to my defaults).
  // Self-contained: this section talks to the API directly, so it needs no
  // wiring from the dashboard.
  const { apiFetch } = useAuth();
  const [msgCalled,  setMsgCalled]  = useState('');
  const [msgNextUp,  setMsgNextUp]  = useState('');
  const [msgDefaults, setMsgDefaults] = useState({ called: '', next_up: '' });
  const [msgOpen,    setMsgOpen]    = useState(false);
  const [msgSaving,  setMsgSaving]  = useState(false);
  const [msgFlash,   setMsgFlash]   = useState('');

  // Load my defaults once the panel is opened, to show as placeholders.
  useEffect(() => {
    if (!msgOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/queue/my-messages');
        if (res.ok && !cancelled) {
          const d = await res.json();
          setMsgDefaults({
            called:  d.msg_called  || d.default_called  || '',
            next_up: d.msg_next_up || d.default_next_up || '',
          });
        }
      } catch { /* placeholders stay blank */ }
    })();
    return () => { cancelled = true; };
  }, [msgOpen, apiFetch]);

  const saveSessionMessages = async () => {
    if (!session) return;
    setMsgSaving(true);
    try {
      const res = await apiFetch(`/api/queue/sessions/${session.session_id}/messages`, {
        method: 'PUT',
        body: JSON.stringify({ msg_called: msgCalled, msg_next_up: msgNextUp }),
      });
      const d = await res.json().catch(() => ({}));
      setMsgFlash(res.ok ? 'Saved for this session.' : (d.detail || 'Could not save.'));
      setTimeout(() => setMsgFlash(''), 3000);
    } catch (e) {
      setMsgFlash(e.message);
    } finally {
      setMsgSaving(false);
    }
  };

  const priorityIds = classPriorityCourseIds || [];

  // Call Next availability: someone waiting, and nobody currently called.
  // (Per-card call buttons are unaffected — out-of-order calling still works.)
  const entryList     = entries || [];
  const waitingCount  = entryList.filter(e => e.status === 'WAITING').length;
  const someoneCalled = entryList.some(e => e.status === 'CALLED');
  const canCallNext   = waitingCount > 0 && !someoneCalled;

  const handleToggleClassPriority = (courseId) => {
    const current = new Set(priorityIds);
    if (current.has(courseId)) current.delete(courseId);
    else current.add(courseId);
    onSetClassPriority(Array.from(current));
  };

  if (!open) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.inner}>

        {/* ── Session control ── */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>Session</div>

          {!session ? (
            <>
              <div className={styles.field}>
                <label>Starting Policy</label>
                <select value={selectedPolicy} onChange={e => setSelectedPolicy(e.target.value)}>
                  {POLICIES.map(p => (
                    <option key={p.value} value={p.value} disabled={p.disabled}>
                      {p.label}{p.disabled ? ' (disabled)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className={`btn-primary ${styles.fullBtn}`}
                onClick={() => onOpenSession(selectedPolicy)}
                disabled={loading}
              >
                {loading ? 'Opening…' : 'Open Queue'}
              </button>
            </>
          ) : (
            <>
              <div className={styles.sessionInfo}>
                <span className={styles.sessionDot} />
                Session #{session.session_id} active
              </div>
              {!confirmClose ? (
                <button className={`btn-danger ${styles.fullBtn}`} onClick={() => setConfirmClose(true)}>
                  Close Session
                </button>
              ) : (
                <div className={styles.confirmRow}>
                  <span className={styles.confirmText}>Are you sure?</span>
                  <button className="btn-danger" onClick={() => { onCloseSession(); setConfirmClose(false); }}>
                    Yes, Close
                  </button>
                  <button className="btn-ghost" onClick={() => setConfirmClose(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {session && (
          <>
            <div className={styles.divider} />

            {/* ── Controls ── */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>Controls</div>
              <div className={styles.toggleRow}>
                <button
                  className={isPaused ? 'btn-primary' : 'btn-secondary'}
                  onClick={onTogglePause}
                  title="Students can still join. Broadcasts a short break message."
                >
                  {isPaused ? '▶ Resume' : '⏸ Pause'}
                </button>
                <button
                  className={isLocked ? 'btn-primary' : 'btn-ghost'}
                  onClick={onToggleLock}
                  title="No new students can join. Students already in queue are unaffected."
                >
                  {isLocked ? '🚪 Door Closed' : '🚪 Close Door'}
                </button>
              </div>
              {isPaused && (
                <p style={{ fontSize: 11, color: 'var(--amber)', marginTop: 6 }}>
                  Students see: "Instructor on a short break — back shortly!"
                </p>
              )}
              {isLocked && (
                <p style={{ fontSize: 11, color: 'var(--amber)', marginTop: 6 }}>
                  New students cannot join. Current queue unaffected.
                </p>
              )}

              <button
                className={`btn-primary ${styles.fullBtn}`}
                onClick={onCallNext}
                disabled={!canCallNext}
                title={
                  someoneCalled
                    ? 'Finish or return the student who is already called first.'
                    : (waitingCount === 0
                        ? 'Nobody is waiting.'
                        : 'Call the student at the top of the queue.')
                }
                style={{ marginTop: 10 }}
              >
                Call Next
              </button>
            </section>

            <div className={styles.divider} />

            {/* ── Pooling status (POOLED_FCFS, pre-shuffle only) ── */}
            {policy === 'POOLED_FCFS' && pooling?.is_pooling && (
              <>
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>Pooling</div>
                  <p style={{ fontSize: 12, color: 'var(--txt-dim)', lineHeight: 1.5, marginBottom: 8 }}>
                    Students are pooling. At the shuffle time everyone waiting is
                    randomized into their starting order{' '}
                    {classPriorityCourseIds?.length > 0 && 'with class priority applied '}
                    — after that it's first-come, first-served.
                  </p>
                  <PoolCountdown deadline={pooling.pool_deadline} />
                  <button
                    className={`btn-primary ${styles.fullBtn}`}
                    onClick={onForceShuffle}
                    style={{ marginTop: 8 }}
                  >
                    Shuffle &amp; Start Now
                  </button>
                </section>

                <div className={styles.divider} />
              </>
            )}

            {/* ── Policy ── */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>Queue Policy</div>
              <div className={styles.policyList}>
                {POLICIES.map(p => (
                  <button
                    key={p.value}
                    className={`${styles.policyBtn} ${policy === p.value ? styles.policyActive : ''}`}
                    onClick={() => { if (!p.disabled) onChangePolicy(p.value); }}
                    disabled={p.disabled}
                    aria-disabled={p.disabled || undefined}
                    title={p.disabled ? 'This policy is temporarily unavailable.' : undefined}
                    style={p.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    {p.label}{p.disabled ? ' (disabled)' : ''}
                    {!p.disabled && policy === p.value && <span className={styles.policyCheck}>✓</span>}
                  </button>
                ))}
              </div>
            </section>



            <div className={styles.divider} />

            {/* ── Class Priority ── */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                Class Priority
                {priorityIds.length > 0 && (
                  <button
                    className="btn-ghost"
                    onClick={() => onSetClassPriority([])}
                    style={{ fontSize: 10, padding: '2px 6px', marginLeft: 8 }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <p style={{ fontSize: 11, color: 'var(--txt-faint)', marginBottom: 8, lineHeight: 1.4 }}>
                Move students from selected courses to the front of the queue.
              </p>
              {courses.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--txt-faint)' }}>No courses available.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {courses.map(c => {
                    const active = priorityIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => handleToggleClassPriority(c.id)}
                        className={active ? 'btn-primary' : 'btn-ghost'}
                        style={{ textAlign: 'left', fontSize: 12, padding: '6px 10px' }}
                      >
                        {active ? '★ ' : '☆ '}{c.course_code}
                        {c.course_name && (
                          <span style={{ color: active ? 'rgba(255,255,255,0.65)' : 'var(--txt-faint)', marginLeft: 6, fontSize: 11 }}>
                            {c.course_name}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Exclusive controls — only meaningful when priority is set */}
              <div style={{ marginTop: 12, opacity: priorityIds.length > 0 ? 1 : 0.5 }}>
                <button
                  className={blockNonPriority ? 'btn-primary' : 'btn-ghost'}
                  onClick={onToggleBlockNonPriority}
                  disabled={priorityIds.length === 0}
                  title={priorityIds.length === 0
                    ? 'Set a priority course first'
                    : 'When on, students not in a priority course cannot join.'}
                  style={{ width: '100%', textAlign: 'left', fontSize: 12, padding: '7px 10px' }}
                >
                  {blockNonPriority ? 'Blocking non-priority joins' : 'Block non-priority joins'}
                </button>
                {blockNonPriority && (
                  <p style={{ fontSize: 11, color: 'var(--amber)', marginTop: 6, lineHeight: 1.4 }}>
                    Only students in priority courses can join right now.
                  </p>
                )}

                <button
                  className="btn-ghost"
                  onClick={onRemoveNonPriority}
                  disabled={priorityIds.length === 0}
                  title={priorityIds.length === 0
                    ? 'Set a priority course first'
                    : 'Remove students already waiting who are not in a priority course.'}
                  style={{ width: '100%', textAlign: 'left', fontSize: 12, padding: '7px 10px', marginTop: 6 }}
                >
                  ✕ Remove waiting non-priority students
                </button>
              </div>
            </section>

            <div className={styles.divider} />

            {/* ── Demo: add test students ── */}
            {testStudents && testStudents.length > 0 && (
              <>
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>Demo</div>
                  <p style={{ fontSize: 11, color: 'var(--txt-faint)', margin: '0 0 8px', lineHeight: 1.4 }}>
                    Add a test student to this queue, then use the normal controls to move them through.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {testStudents.map(ts => (
                      <div key={ts.id} style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn-ghost"
                          onClick={() => onAddTestStudent(ts)}
                          disabled={!ts.courses || ts.courses.length === 0}
                          title={!ts.courses || ts.courses.length === 0
                            ? 'Enroll this student in a course first (Manage tab)'
                            : `Add ${ts.display_name} to the queue`}
                          style={{ flex: 1, textAlign: 'left', fontSize: 12, padding: '6px 10px' }}
                        >
                          + {ts.display_name}
                          {(!ts.courses || ts.courses.length === 0) && (
                            <span style={{ color: 'var(--txt-faint)', fontSize: 10 }}> (no courses)</span>
                          )}
                        </button>
                        <button
                          className="btn-ghost"
                          onClick={() => onViewAsTestStudent(ts)}
                          title={`Open the student view as ${ts.display_name} in a new tab`}
                          style={{ fontSize: 11, padding: '6px 8px', whiteSpace: 'nowrap' }}
                        >
                          View as
                        </button>
                      </div>
                    ))}
                  </div>
                </section>

                <div className={styles.divider} />
              </>
            )}

            {/* ── Session messages (override my defaults, this session only) ── */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                Session Messages
                <button
                  className="btn-ghost"
                  onClick={() => setMsgOpen(o => !o)}
                  style={{ fontSize: 10, padding: '2px 6px', marginLeft: 8 }}
                >
                  {msgOpen ? 'Hide' : 'Edit'}
                </button>
              </div>
              {msgOpen && (
                <>
                  <p style={{ fontSize: 11, color: 'var(--txt-faint)', margin: '0 0 8px', lineHeight: 1.4 }}>
                    Override what students see for this session only. Blank uses your
                    defaults from the Manage tab.
                  </p>
                  {msgFlash && (
                    <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 6 }}>{msgFlash}</div>
                  )}
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, color: 'var(--txt-faint)', display: 'block', marginBottom: 3 }}>
                      When called
                    </label>
                    <input
                      type="text"
                      value={msgCalled}
                      maxLength={300}
                      placeholder={msgDefaults.called || 'Use my default'}
                      onChange={e => setMsgCalled(e.target.value)}
                      style={{ width: '100%', fontSize: 12, padding: '5px 8px' }}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, color: 'var(--txt-faint)', display: 'block', marginBottom: 3 }}>
                      When next up
                    </label>
                    <input
                      type="text"
                      value={msgNextUp}
                      maxLength={300}
                      placeholder={msgDefaults.next_up || 'Use my default'}
                      onChange={e => setMsgNextUp(e.target.value)}
                      style={{ width: '100%', fontSize: 12, padding: '5px 8px' }}
                    />
                  </div>
                  <button
                    className={`btn-secondary ${styles.fullBtn}`}
                    onClick={saveSessionMessages}
                    disabled={msgSaving}
                  >
                    {msgSaving ? 'Saving…' : 'Save for this session'}
                  </button>
                </>
              )}
            </section>

            <div className={styles.divider} />

            {/* ── Announcement ── */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>Announcement</div>
              <textarea
                className={styles.announcementInput}
                placeholder="Message to all students…"
                value={announcementText}
                onChange={e => setAnnouncementText(e.target.value)}
                rows={3}
              />
              <button
                className={`btn-secondary ${styles.fullBtn}`}
                onClick={() => { onAnnounce(announcementText.trim()); setAnnouncementText(''); }}
                disabled={!announcementText.trim()}
              >
                Broadcast
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
